import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  getSOVLines,
  getTaskAllocations,
  getSOVAllocations,
  createAllocation,
  deleteAllocation,
  getSections,
  createSection,
  reorderSections,
  addPredecessor,
  updatePredecessor,
  removePredecessor,
  getProgressAlertsSummary,
  getDependencyAlerts
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';
import '../App.css';

// Helper function to format dates as MM-DD-YYYY
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Task type color mapping matching Base44 design
const getTaskTypeStyle = (taskType) => {
  switch ((taskType || '').toLowerCase()) {
    case 'material':
    case 'procurement':
      return { backgroundColor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D' };
    case 'labor':
    case 'work':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'engineering':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    case 'inspection':
      return { backgroundColor: '#F3E5F5', color: '#6A1B9A', border: '1px solid #BA68C8' };
    case 'milestone':
      return { backgroundColor: '#FFF8E1', color: '#F57F17', border: '1px solid #FFD54F' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#616161', border: '1px solid #BDBDBD' };
  }
};

// Status badge style
const getStatusStyle = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'not started':
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
    case 'in progress':
      return { backgroundColor: '#E3F2FD', color: '#1565C0', border: '1px solid #64B5F6' };
    case 'complete':
      return { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #81C784' };
    default:
      return { backgroundColor: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' };
  }
};

// Generate section prefix from building_area_phase (e.g., "Elementary School" -> "ES")
const getSectionPrefix = (section) => {
  if (!section || section === 'Unsectioned') return 'T';
  const words = section.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
};

// Generate task code from section prefix + index (1-based, 3-digit padded)
const generateTaskCode = (section, index) => {
  const prefix = getSectionPrefix(section);
  return `${prefix}-${String(index).padStart(3, '0')}`;
};

const DEP_TYPES = [
  { value: 'FS', label: 'FS — Finish to Start', short: 'FS' },
  { value: 'SS', label: 'SS — Start to Start', short: 'SS' },
  { value: 'FF', label: 'FF — Finish to Finish', short: 'FF' },
  { value: 'SF', label: 'SF — Start to Finish', short: 'SF' },
];

// Predecessor section component matching Base44 design
// Supports two modes:
//   "api" mode (default): saves/removes via addPredecessor/removePredecessor API (for Task Properties)
//   "local" mode: manages predecessors in local state, returns them via onLocalChange (for Add Task modal)
const PredecessorSection = ({ currentTask, tasks, editingTaskId, inputStyle, fieldGroup, addPredecessor, updatePredecessor, removePredecessor, fetchTasks, confirm, mode = 'api', localPredecessors = [], onLocalChange }) => {
  const t = useTheme();
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPredTask, setSelectedPredTask] = useState(null);
  const [depType, setDepType] = useState('FS');
  const [lagDays, setLagDays] = useState(0);

  const isLocal = mode === 'local';
  const currentPredecessors = isLocal
    ? localPredecessors
    : (currentTask?.predecessors || []).filter(p => p.predecessor_task_id != null);
  const alreadyLinkedIds = new Set(currentPredecessors.map(p => Number(p.predecessor_task_id)));

  // Available tasks: exclude self and already-linked
  const availableTasks = tasks
    .filter(t => t.id !== editingTaskId && !alreadyLinkedIds.has(Number(t.id)))
    .sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));

  // Filter by search text
  const filteredTasks = searchText.trim()
    ? availableTasks.filter(t => {
        const q = searchText.toLowerCase();
        return (t.task_code || '').toLowerCase().includes(q) ||
               (t.task_name || '').toLowerCase().includes(q) ||
               (t.building_area_phase || '').toLowerCase().includes(q);
      })
    : availableTasks;

  const handleSelectTask = async (task) => {
    if (isLocal) {
      // In local mode (Add Task), immediately add with defaults
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: task.id,
        dependency_type: 'FS',
        lag_days: 0,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSearchText('');
      setShowDropdown(false);
    } else {
      // In API mode (Edit Task), immediately add with FS defaults (matching local mode behavior)
      setSearchText('');
      setShowDropdown(false);
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: task.id,
          dependency_type: 'FS',
          lag_days: 0,
        });
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleAdd = async () => {
    if (!selectedPredTask) return;
    if (isLocal) {
      const newPred = {
        id: `local_${Date.now()}`,
        predecessor_task_id: selectedPredTask.id,
        dependency_type: depType,
        lag_days: lagDays,
      };
      onLocalChange([...localPredecessors, newPred]);
      setSelectedPredTask(null);
      setDepType('FS');
      setLagDays(0);
    } else {
      try {
        await addPredecessor(editingTaskId, {
          predecessor_task_id: selectedPredTask.id,
          dependency_type: depType,
          lag_days: lagDays,
        });
        setSelectedPredTask(null);
        setDepType('FS');
        setLagDays(0);
        fetchTasks({ preserveScroll: true });
      } catch (err) {
        console.error('Error adding predecessor:', err);
        alert('Failed to add predecessor: ' + err.message);
      }
    }
  };

  const handleRemove = async (predId) => {
    if (isLocal) {
      onLocalChange(localPredecessors.filter(p => p.id !== predId));
    } else {
      const ok = await confirm({ title: 'Remove Predecessor', message: 'Are you sure you want to remove this predecessor link?', confirmLabel: 'Remove', danger: true });
      if (!ok) return;
      try {
        await removePredecessor(editingTaskId, predId);
        fetchTasks({ preserveScroll: true });
      } catch (err) { console.error('Error removing predecessor:', err); }
    }
  };

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
    marginBottom: '6px', border: `1px solid ${t.cardBorder}`,
  };

  return (
    <div style={fieldGroup}>
      {/* Existing predecessors */}
      {currentPredecessors.map(pred => {
        const predTask = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
        const dt = DEP_TYPES.find(d => d.value === pred.dependency_type) || DEP_TYPES[0];
        return (
          <div key={pred.id} style={{ ...rowStyle, flexWrap: 'wrap' }}>
            <div style={{
              flex: 1, fontSize: '13px', fontWeight: 500, color: t.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: '120px',
            }}>
              <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
                {predTask?.task_code || '?'}
              </span>
              {predTask ? predTask.task_name : `Task #${pred.predecessor_task_id}`}
              {predTask && (predTask.start_date || predTask.finish_date) && (
                <div style={{ fontSize: '11px', color: t.textMuted, fontWeight: 400, marginTop: '2px', whiteSpace: 'nowrap' }}>
                  {predTask.start_date ? `Start: ${fmtDate(predTask.start_date)}` : ''}
                  {predTask.start_date && predTask.finish_date ? '  —  ' : ''}
                  {predTask.finish_date ? `Finish: ${fmtDate(predTask.finish_date)}` : ''}
                </div>
              )}
            </div>
            <select value={pred.dependency_type || 'FS'}
              onChange={(e) => {
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, dependency_type: e.target.value } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: e.target.value, lag_days: pred.lag_days || 0 })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                padding: '4px 6px', fontSize: '12px', border: `1px solid ${t.cardBorder}`,
                borderRadius: '6px', backgroundColor: t.bg, color: t.text,
                width: '130px', flexShrink: 0, cursor: 'pointer',
              }}>
              {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <span style={{ fontSize: '11px', color: t.textSecondary, flexShrink: 0 }}>Lag</span>
            <input type="number" value={pred.lag_days || 0}
              onChange={(e) => {
                const newLag = parseInt(e.target.value) || 0;
                if (isLocal) {
                  onLocalChange(localPredecessors.map(p => p.id === pred.id ? { ...p, lag_days: newLag } : p));
                } else {
                  updatePredecessor(editingTaskId, pred.id, { dependency_type: pred.dependency_type || 'FS', lag_days: newLag })
                    .then(() => fetchTasks({ preserveScroll: true })).catch(err => console.error('Error updating predecessor:', err));
                }
              }}
              style={{
                width: '40px', padding: '4px 6px', fontSize: '12px', textAlign: 'center',
                border: `1px solid ${t.cardBorder}`, borderRadius: '6px', backgroundColor: t.bg,
              }} />
            <span style={{ fontSize: '11px', color: t.textSecondary, flexShrink: 0 }}>d</span>
            <button type="button" onClick={() => handleRemove(pred.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                color: t.textMuted, fontSize: '16px', lineHeight: 1, flexShrink: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
              title="Remove predecessor"
            >&times;</button>
          </div>
        );
      })}

      {/* Selected task — show dep type + lag + confirm */}
      {selectedPredTask && (
        <div style={{ ...rowStyle, border: '1px solid #3b82f6', backgroundColor: '#eff6ff' }}>
          <div style={{
            flex: 1, fontSize: '13px', fontWeight: 500, color: t.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '6px' }}>
              {selectedPredTask.task_code}
            </span>
            {selectedPredTask.task_name}
          </div>
          <select value={depType} onChange={(e) => setDepType(e.target.value)}
            style={{
              padding: '4px 6px', fontSize: '12px', border: '1px solid #93c5fd',
              borderRadius: '6px', backgroundColor: t.bg, color: t.text,
              width: '130px', flexShrink: 0,
            }}>
            {DEP_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <span style={{ fontSize: '11px', color: '#64748b', flexShrink: 0 }}>Lag</span>
          <input type="number" value={lagDays} onChange={(e) => setLagDays(parseInt(e.target.value) || 0)} min={0}
            style={{
              width: '40px', padding: '4px 6px', fontSize: '12px', textAlign: 'center',
              border: '1px solid #93c5fd', borderRadius: '6px', backgroundColor: '#fff',
            }} />
          <span style={{ fontSize: '11px', color: '#64748b', flexShrink: 0 }}>d</span>
          <button type="button" onClick={handleAdd}
            style={{
              background: '#3b82f6', border: 'none', color: '#fff', borderRadius: '6px',
              padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            }}>Add</button>
          <button type="button" onClick={() => setSelectedPredTask(null)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
              color: '#94a3b8', fontSize: '16px', lineHeight: 1, flexShrink: 0,
            }}>&times;</button>
        </div>
      )}

      {/* Search / Add dropdown — only show when no task is selected */}
      {!selectedPredTask && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Search tasks to add as predecessor..."
              style={{
                ...inputStyle, fontSize: '13px', paddingRight: '32px',
              }}
            />
            <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          {showDropdown && (
            <>
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                backgroundColor: t.bg, border: `1px solid ${t.cardBorder}`, borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '200px', overflowY: 'auto',
                marginTop: '4px',
              }}>
                {filteredTasks.length === 0 ? (
                  <div style={{ padding: '12px 14px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                    No matching tasks
                  </div>
                ) : (
                  filteredTasks.map(t => (
                    <div key={t.id}
                      onClick={() => handleSelectTask(t)}
                      style={{
                        padding: '8px 14px', fontSize: '13px', cursor: 'pointer',
                        borderBottom: `1px solid ${t.tableBorder}`,
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '8px' }}>{t.task_code}</span>
                      <span style={{ color: t.text }}>{t.task_name}</span>
                      {t.building_area_phase && (
                        <span style={{ color: t.textMuted, fontSize: '11px', marginLeft: '8px' }}>({t.building_area_phase})</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const TasksTab = ({ projectId, refreshSummary }) => {
  const t = useTheme();
  const { confirm, confirmDialogProps } = useConfirm();
  const [tasks, setTasks] = useState([]);
  const [sovLines, setSovLines] = useState([]);
  const [taskAllocations, setTaskAllocations] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editingPercentCompleteId, setEditingPercentCompleteId] = useState(null);
  const [editingPercentCompleteValue, setEditingPercentCompleteValue] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  // SOV linking panel - which task has the link panel open
  const [linkingTaskId, setLinkingTaskId] = useState(null);
  // Drag and drop state (tasks)
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  // Drag and drop state (sections)
  const [draggedSectionName, setDraggedSectionName] = useState(null);
  const [dragOverSectionName, setDragOverSectionName] = useState(null);
  const sectionDragRef = useRef(false); // synchronous flag for section drag
  // Persisted sections from backend
  const [savedSections, setSavedSections] = useState([]);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  // Add Section modal state
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [flaggedTaskIds, setFlaggedTaskIds] = useState(new Set());
  const [depAlertData, setDepAlertData] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  // Pending predecessors for the Add Task modal (saved after task is created)
  const [pendingPredecessors, setPendingPredecessors] = useState([]);
  const [newSectionName, setNewSectionName] = useState('');
  const [sectionNameError, setSectionNameError] = useState('');
  const [formData, setFormData] = useState({
    task_code: '',
    task_name: '',
    task_description: '',
    building_area_phase: '',
    task_type: 'Labor',
    start_date: '',
    finish_date: '',
    duration_days: 0,
    responsible_party: '',
    client_visible: true,
    milestone_flag: false,
    notes: ''
  });

  // Helper: get all existing section names (from tasks + persisted sections)
  const getExistingSections = () => {
    const sections = new Set();
    tasks.forEach(t => {
      const sec = (t.building_area_phase || '').trim();
      if (sec) sections.add(sec);
    });
    savedSections.forEach(s => sections.add(s.section_name));
    return Array.from(sections);
  };

  // Check if a section name already exists (case-insensitive), optionally excluding a specific original name
  const isSectionNameTaken = (name, excludeOriginal = null) => {
    const normalized = (name || '').trim().toLowerCase();
    if (!normalized) return false;
    return getExistingSections().some(sec => {
      if (excludeOriginal && sec.toLowerCase() === excludeOriginal.toLowerCase()) return false;
      return sec.toLowerCase() === normalized;
    });
  };

  // Fetch sections from backend
  const fetchSections = async () => {
    try {
      const data = await getSections(projectId);
      console.log('[fetchSections] Loaded:', data.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(data);
      setSectionsLoaded(true);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSectionsLoaded(true);
    }
  };

  // Fetch tasks, SOV lines, sections, and progress alerts on mount
  useEffect(() => {
    fetchTasks();
    fetchSOVLines();
    fetchSections();
    getProgressAlertsSummary(projectId).then(data => {
      const ids = new Set();
      (data.summaries || []).forEach(s => (s.flagged_task_ids || []).forEach(id => ids.add(id)));
      setFlaggedTaskIds(ids);
    }).catch(console.error);
    getDependencyAlerts(projectId).then(data => {
      setDepAlertData(data);
    }).catch(console.error);
  }, [projectId]);

  // Renumber all tasks in all sections so codes are sequential starting at 001
  const renumberAllSections = async (taskList) => {
    const grouped = {};
    taskList.forEach(t => {
      const sec = t.building_area_phase || 'Unsectioned';
      if (!grouped[sec]) grouped[sec] = [];
      grouped[sec].push(t);
    });

    let needsRefresh = false;
    for (const section of Object.keys(grouped)) {
      // Sort by existing code so we preserve current order
      grouped[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
      for (let i = 0; i < grouped[section].length; i++) {
        const correctCode = generateTaskCode(section, i + 1);
        if (grouped[section][i].task_code !== correctCode) {
          try {
            await updateTask(grouped[section][i].id, { task_code: correctCode });
            grouped[section][i].task_code = correctCode;
            needsRefresh = true;
          } catch (e) { /* ignore */ }
        }
      }
    }
    return needsRefresh;
  };

  const fetchTasks = async ({ preserveScroll = false } = {}) => {
    // Save scroll positions before re-fetching so we can restore them
    const savedWindowScroll = preserveScroll ? window.scrollY : 0;
    const scrollableEls = preserveScroll ? Array.from(document.querySelectorAll('[style*="overflow"]')).filter(el => el.scrollTop > 0).map(el => ({ el, top: el.scrollTop })) : [];

    try {
      // Only show loading spinner on initial load, not on preserveScroll refreshes
      if (!preserveScroll) setLoading(true);

      let data = await getTasks(projectId);

      // Auto-renumber if any codes are out of sequence
      const didRenumber = await renumberAllSections(data);
      if (didRenumber) {
        data = await getTasks(projectId);
      }

      setTasks(data);
      // Fetch allocations for all tasks to populate SOV Links column
      const allocMap = {};
      for (const task of data) {
        try {
          const allocs = await getTaskAllocations(task.id);
          allocMap[task.id] = allocs;
        } catch (e) { /* ignore */ }
      }
      setTaskAllocations(prev => ({ ...prev, ...allocMap }));

      // Restore scroll positions after React re-renders with new data
      if (preserveScroll) {
        // Double rAF ensures we restore AFTER React has committed the DOM update
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedWindowScroll);
            scrollableEls.forEach(({ el, top }) => { if (document.contains(el)) el.scrollTop = top; });
          });
        });
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      if (!preserveScroll) setLoading(false);
    }
  };

  const fetchSOVLines = async () => {
    try {
      const data = await getSOVLines(projectId);
      setSovLines(data);
    } catch (error) {
      console.error('Error fetching SOV lines:', error);
    }
  };

  const fetchTaskAllocations = async (taskId) => {
    try {
      const data = await getTaskAllocations(taskId);
      setTaskAllocations(prev => ({ ...prev, [taskId]: data }));
    } catch (error) {
      console.error('Error fetching task allocations:', error);
    }
  };

  // Auto-calculate duration_days when dates change
  useEffect(() => {
    if (formData.start_date && formData.finish_date) {
      const start = new Date(formData.start_date);
      const finish = new Date(formData.finish_date);
      const duration = Math.ceil((finish - start) / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, duration_days: Math.max(0, duration) }));
    }
  }, [formData.start_date, formData.finish_date]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    // When start date changes and finish date is before the new start, clear finish date
    if (name === 'start_date' && value && formData.finish_date && formData.finish_date < value) {
      newData.finish_date = '';
    }
    setFormData(prev => ({ ...prev, ...newData }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddTask = async () => {
    const errors = {};
    if (!formData.task_name || !formData.task_name.trim()) errors.task_name = 'Task name is required';
    if (!formData.start_date) errors.start_date = 'Start date is required';
    if (!formData.finish_date) errors.finish_date = 'Finish date is required';
    if (formData.start_date && formData.finish_date) {
      if (new Date(formData.finish_date) < new Date(formData.start_date)) {
        errors.finish_date = 'Finish date cannot be before start date';
      }
    }
    if (!formData.building_area_phase) errors.building_area_phase = 'Please select a section';

    // Validate dates against predecessor dependencies
    if (pendingPredecessors.length > 0 && formData.start_date) {
      for (const pred of pendingPredecessors) {
        const predTask = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
        if (!predTask) continue;
        const lag = pred.lag_days || 0;
        const addDays = (dateStr, days) => {
          const d = new Date(dateStr + 'T00:00:00');
          d.setDate(d.getDate() + days);
          return d;
        };

        switch (pred.dependency_type) {
          case 'FS': // Finish to Start: new start >= pred finish + lag
            if (predTask.finish_date && formData.start_date) {
              const earliest = addDays(predTask.finish_date, lag);
              if (new Date(formData.start_date + 'T00:00:00') < earliest) {
                errors.start_date = `Start date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} finishes ${new Date(predTask.finish_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'SS': // Start to Start: new start >= pred start + lag
            if (predTask.start_date && formData.start_date) {
              const earliest = addDays(predTask.start_date, lag);
              if (new Date(formData.start_date + 'T00:00:00') < earliest) {
                errors.start_date = `Start date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} starts ${new Date(predTask.start_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'FF': // Finish to Finish: new finish >= pred finish + lag
            if (predTask.finish_date && formData.finish_date) {
              const earliest = addDays(predTask.finish_date, lag);
              if (new Date(formData.finish_date + 'T00:00:00') < earliest) {
                errors.finish_date = `Finish date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} finishes ${new Date(predTask.finish_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          case 'SF': // Start to Finish: new finish >= pred start + lag
            if (predTask.start_date && formData.finish_date) {
              const earliest = addDays(predTask.start_date, lag);
              if (new Date(formData.finish_date + 'T00:00:00') < earliest) {
                errors.finish_date = `Finish date must be on or after ${earliest.toLocaleDateString('en-US')} (${predTask.task_code} starts ${new Date(predTask.start_date + 'T00:00:00').toLocaleDateString('en-US')}${lag ? ` + ${lag}d lag` : ''})`;
              }
            }
            break;
          default:
            break;
        }
        // Stop at the first violation so the message is clear
        if (errors.start_date || errors.finish_date) break;
      }
    }

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    try {
      // Auto-assign task code based on position in section
      const section = formData.building_area_phase || 'Unsectioned';
      const sectionTasks = tasks.filter(t => (t.building_area_phase || 'Unsectioned') === section);
      const newCode = generateTaskCode(section, sectionTasks.length + 1);

      // Calculate duration
      let duration = 0;
      if (formData.start_date && formData.finish_date) {
        const s = new Date(formData.start_date);
        const f = new Date(formData.finish_date);
        duration = Math.max(1, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
      }

      const taskData = {
        project_id: projectId,
        task_code: newCode,
        task_name: formData.task_name,
        task_description: formData.task_description || '',
        building_area_phase: formData.building_area_phase,
        task_type: formData.task_type || 'Labor',
        start_date: formData.start_date,
        finish_date: formData.finish_date,
        duration_days: duration,
        responsible_party: formData.responsible_party || '',
        client_visible: formData.client_visible ? 1 : 0,
        milestone_flag: formData.milestone_flag ? 1 : 0,
        notes: formData.notes || '',
      };

      const newTask = await createTask(taskData);

      // Save any pending predecessors
      if (pendingPredecessors.length > 0 && newTask?.id) {
        for (const pred of pendingPredecessors) {
          try {
            await addPredecessor(newTask.id, {
              predecessor_task_id: pred.predecessor_task_id,
              dependency_type: pred.dependency_type,
              lag_days: pred.lag_days,
            });
          } catch (predErr) {
            console.error('Error adding predecessor:', predErr);
          }
        }
      }

      setShowModal(false);
      setFormErrors({});
      setPendingPredecessors([]);
      setFormData({
        task_code: '', task_name: '', task_description: '', building_area_phase: '',
        task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0,
        responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
      });
      fetchTasks();
      refreshSummary();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task: ' + error.message);
    }
  };

  const handleUpdateTask = async (taskId, updates, { preserveScroll = false } = {}) => {
    try {
      await updateTask(taskId, updates);
      fetchTasks({ preserveScroll });
      refreshSummary();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    const ok = await confirm({ title: 'Delete Task', message: 'Are you sure you want to delete this task? This action cannot be undone.', confirmLabel: 'Delete', danger: true });
    if (ok) {
      try {
        // Find the task's section before deleting
        const taskToDelete = tasks.find(t => t.id === taskId);
        const section = taskToDelete ? (taskToDelete.building_area_phase || 'Unsectioned') : null;

        await deleteTask(taskId);
        setEditingTaskId(null);
        setLinkingTaskId(null);

        // Fetch fresh task list, then renumber the section
        const freshTasks = await getTasks(projectId);
        setTasks(freshTasks);

        if (section) {
          const sectionTasks = freshTasks
            .filter(t => (t.building_area_phase || 'Unsectioned') === section)
            .sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));

          for (let i = 0; i < sectionTasks.length; i++) {
            const newCode = generateTaskCode(section, i + 1);
            if (sectionTasks[i].task_code !== newCode) {
              await updateTask(sectionTasks[i].id, { task_code: newCode });
            }
          }
          // Refresh again to show updated codes
          fetchTasks({ preserveScroll: true });
        }
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  // Calculate max allowed progress for a task based on schedule proration
  const getMaxProgress = (task) => {
    if (!task || !task.start_date || !task.finish_date) return 100;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const taskStart = new Date(task.start_date + 'T00:00:00');
    const taskEnd = new Date(task.finish_date + 'T23:59:59');
    const dayMs = 1000 * 60 * 60 * 24;
    const totalDuration = Math.max(1, Math.round((taskEnd - taskStart) / dayMs) + 1);
    if (today < taskStart) return 0;
    if (today >= taskEnd) return 100;
    const daysElapsed = Math.max(1, Math.round((today - taskStart) / dayMs) + 1);
    return Math.min(100, Math.ceil((daysElapsed / totalDuration) * 100));
  };

  const handlePercentCompleteChange = async (taskId, newValue) => {
    const task = tasks.find(tk => tk.id === taskId);
    const maxPct = getMaxProgress(task);
    // Hard clamp: never allow more than the prorated max
    const percentComplete = Math.min(maxPct, Math.max(0, parseInt(newValue) || 0));

    // Show warning if user tried to exceed the cap
    if (parseInt(newValue) > maxPct && maxPct < 100) {
      if (maxPct === 0) {
        confirm({
          title: 'Cannot Set Progress',
          message: `This task is scheduled entirely in the future (starts ${task.start_date}). Progress must remain at 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        confirm({
          title: 'Progress Capped',
          message: `This task is scheduled ${task.start_date} – ${task.finish_date}. Based on today's date, only ${maxPct}% of the schedule has elapsed. Maximum allowed progress: ${maxPct}%. To enter more, adjust the task finish date.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
      // Still save the clamped value (don't return early) so the UI stays consistent
      if (percentComplete === (parseFloat(task?.percent_complete) || 0)) return;
    }

    // Determine new status based on percentage
    let newStatus = 'Not Started';
    if (percentComplete >= 100) newStatus = 'Complete';
    else if (percentComplete > 0) newStatus = 'In Progress';
    // Optimistic update: patch local state immediately so no re-fetch/re-render flicker
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, percent_complete: percentComplete, status: newStatus } : t
    ));
    // Save to server in the background — no fetchTasks, no await
    updateTask(taskId, { percent_complete: percentComplete, status: newStatus })
      .then(() => refreshSummary())
      .catch((error) => {
        console.error('Error updating task:', error);
        fetchTasks({ preserveScroll: true });
      });
  };

  // ============ DRAG AND DROP ============
  const handleDragStart = (e, task, section) => {
    if (sectionDragRef.current) { e.preventDefault(); return; } // Don't start task drag during section drag
    setDraggedTask(task);
    setDragSection(section);
    e.dataTransfer.setData('text/plain', 'task:' + task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
    setDragOverTask(null);
    setDragSection(null);
  };

  const handleDragOver = (e, task) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== dragOverTask) {
      setDragOverTask(task.id);
    }
  };

  const handleDrop = async (e, targetTask, section) => {
    if (sectionDragRef.current) return; // Section drag in progress — let it bubble
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Only allow reorder within same section
    if (dragSection !== section) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    const sectionTasks = [...(groupedTasks[section] || [])];
    const dragIdx = sectionTasks.findIndex(t => t.id === draggedTask.id);
    const dropIdx = sectionTasks.findIndex(t => t.id === targetTask.id);

    if (dragIdx === -1 || dropIdx === -1) return;

    // Reorder: remove dragged, insert at drop position
    const [moved] = sectionTasks.splice(dragIdx, 1);
    sectionTasks.splice(dropIdx, 0, moved);

    // Build new codes for the reordered section
    const updates = sectionTasks.map((task, idx) => ({
      id: task.id,
      newCode: generateTaskCode(section, idx + 1)
    }));

    // Build the full reordered task list for optimistic UI
    // Keep tasks from other sections in place, replace this section's tasks in new order
    const otherTasks = tasks.filter(t => (t.building_area_phase || 'Unsectioned') !== section);
    const reorderedSectionTasks = sectionTasks.map((t, idx) => ({
      ...t,
      task_code: updates[idx].newCode
    }));
    setTasks([...otherTasks, ...reorderedSectionTasks]);

    setDraggedTask(null);
    setDragOverTask(null);

    // Persist to backend (no fetchTasks — avoids scroll reset)
    for (const upd of updates) {
      try {
        await updateTask(upd.id, { task_code: upd.newCode });
      } catch (err) {
        console.error('Error updating task code:', err);
      }
    }
  };

  // ============ SECTION DRAG AND DROP ============
  const handleSectionDragStart = (e, sectionName) => {
    e.stopPropagation();
    sectionDragRef.current = true;
    setDraggedSectionName(sectionName);
    e.dataTransfer.setData('text/plain', 'section:' + sectionName);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleSectionDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);
  };

  const handleSectionDragOver = (e, sectionName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (sectionName !== dragOverSectionName) {
      setDragOverSectionName(sectionName);
    }
  };

  const sectionsRef = useRef([]);

  const handleSectionDrop = async (e, targetSection) => {
    e.preventDefault();
    e.stopPropagation();

    const dragged = draggedSectionName;
    sectionDragRef.current = false;
    setDraggedSectionName(null);
    setDragOverSectionName(null);

    if (!dragged || dragged === targetSection) return;

    const currentOrder = [...sectionsRef.current];
    const dragIdx = currentOrder.indexOf(dragged);
    const dropIdx = currentOrder.indexOf(targetSection);
    if (dragIdx < 0 || dropIdx < 0) return;

    // Reorder
    currentOrder.splice(dragIdx, 1);
    currentOrder.splice(dropIdx, 0, dragged);

    // Optimistic UI — update savedSections immediately so render reflects new order
    const optimistic = currentOrder.map((name, i) => {
      const existing = savedSections.find(s => s.section_name === name);
      return existing ? { ...existing, sort_order: i } : { section_name: name, sort_order: i, project_id: projectId };
    });
    setSavedSections(optimistic);

    // Persist to backend
    try {
      console.log('[SectionDrop] Saving order:', currentOrder);
      const updated = await reorderSections(projectId, currentOrder);
      console.log('[SectionDrop] Server returned:', updated.map(s => `${s.section_name}(${s.sort_order})`));
      setSavedSections(updated);
    } catch (err) {
      console.error('[SectionDrop] Error reordering sections:', err);
    }
  };

  // ============ SOV LINKING ============
  const handleLinkSOV = async (taskId, sovLineId, allocationAmount) => {
    try {
      await createAllocation({
        project_id: projectId,
        task_id: taskId,
        sov_line_id: sovLineId,
        allocation_amount: parseFloat(allocationAmount),
        allocation_method: 'Amount'
      });
      await fetchTaskAllocations(taskId);
    } catch (error) {
      console.error('Error linking SOV:', error);
    }
  };

  const handleRemoveAllocation = async (taskId, allocationId) => {
    const ok = await confirm({ title: 'Remove Allocation', message: 'Are you sure you want to remove this SOV allocation?', confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await deleteAllocation(allocationId);
      await fetchTaskAllocations(taskId);
    } catch (error) {
      console.error('Error removing allocation:', error);
    }
  };

  // ============ SOV LINK MODAL DIALOG ============
  const SOVLinkModal = ({ task, onClose }) => {
    const [sovLineId, setSovLineId] = useState('');
    const [allocationType, setAllocationType] = useState('Percentage');
    const [allocationValue, setAllocationValue] = useState('100');
    const [searchQuery, setSearchQuery] = useState('');
    const [showSovDropdown, setShowSovDropdown] = useState(false);
    const [sovAllocatedMap, setSovAllocatedMap] = useState({});
    const sovInputRef = React.useRef(null);
    const [sovDropdownPos, setSovDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const allocations = taskAllocations[task.id] || [];

    // On mount, fetch total allocated for each SOV line
    useEffect(() => {
      const fetchAllocated = async () => {
        const map = {};
        for (const line of sovLines) {
          try {
            const allocs = await getSOVAllocations(line.id);
            const totalAllocated = allocs.reduce((sum, a) => sum + (parseFloat(a.allocation_amount) || 0), 0);
            map[line.id] = totalAllocated;
          } catch (e) { map[line.id] = 0; }
        }
        setSovAllocatedMap(map);
      };
      fetchAllocated();
    }, []);

    // Refresh allocated map after add/remove
    const refreshAllocated = async (lineId) => {
      try {
        const allocs = await getSOVAllocations(lineId);
        const totalAllocated = allocs.reduce((sum, a) => sum + (parseFloat(a.allocation_amount) || 0), 0);
        setSovAllocatedMap(prev => ({ ...prev, [lineId]: totalAllocated }));
      } catch (e) { /* ignore */ }
    };

    const filteredSovLines = sovLines.filter(line => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (line.sov_code || '').toLowerCase().includes(q) ||
             (line.description || '').toLowerCase().includes(q) ||
             (line.building_area_phase || '').toLowerCase().includes(q);
    });

    // Compute remaining % / $ for the selected SOV line
    const selectedSovLine = sovLineId ? sovLines.find(l => l.id === parseInt(sovLineId)) : null;
    const selectedContractAmt = selectedSovLine ? (parseFloat(selectedSovLine.contract_amount) || 0) : 0;
    const selectedTotalAllocated = selectedSovLine ? (sovAllocatedMap[selectedSovLine.id] || 0) : 0;
    const selectedRemainingAmt = selectedContractAmt - selectedTotalAllocated;
    const selectedRemainingPct = selectedContractAmt > 0 ? (selectedRemainingAmt / selectedContractAmt) * 100 : 0;

    // Check if current input would exceed 100%
    const requestedPercent = (() => {
      if (!sovLineId || !allocationValue) return 0;
      if (allocationType === 'Percentage') return parseFloat(allocationValue) || 0;
      if (selectedContractAmt > 0) return ((parseFloat(allocationValue) || 0) / selectedContractAmt) * 100;
      return 0;
    })();
    const wouldExceed = sovLineId && allocationValue && requestedPercent > Math.round(selectedRemainingPct * 100) / 100 + 0.01;

    const handleAddLink = async () => {
      if (!sovLineId || !allocationValue) return;
      const selectedLine = sovLines.find(l => l.id === parseInt(sovLineId));
      if (!selectedLine) return;

      let amount = 0;
      let percent = null;
      if (allocationType === 'Percentage') {
        percent = parseFloat(allocationValue);
        amount = (selectedLine.contract_amount * percent) / 100;
      } else {
        amount = parseFloat(allocationValue);
        percent = selectedLine.contract_amount > 0 ? (amount / selectedLine.contract_amount) * 100 : 0;
      }

      // Validate: don't exceed 100%
      const contractAmt = parseFloat(selectedLine.contract_amount) || 0;
      const totalAllocated = sovAllocatedMap[selectedLine.id] || 0;
      const remainingAmt = contractAmt - totalAllocated;
      if (amount > remainingAmt + 0.01) {
        alert(`Cannot allocate more than the remaining $${remainingAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })} (${contractAmt > 0 ? Math.round((remainingAmt / contractAmt) * 100) : 0}%) for this SOV line.`);
        return;
      }

      try {
        await createAllocation({
          project_id: projectId,
          task_id: task.id,
          sov_line_id: parseInt(sovLineId),
          allocation_method: allocationType,
          allocation_percent: percent,
          allocation_amount: amount,
        });
        await fetchTaskAllocations(task.id);
        await refreshAllocated(parseInt(sovLineId));
        setSovLineId('');
        setAllocationValue(allocationType === 'Percentage' ? '100' : '');
        setSearchQuery('');
      } catch (error) {
        console.error('Error adding link:', error);
      }
    };

    const handleRemoveLink = async (alloc) => {
      const ok = await confirm({ title: 'Remove SOV Link', message: 'Are you sure you want to remove this SOV link?', confirmLabel: 'Remove', danger: true });
      if (!ok) return;
      try {
        await deleteAllocation(alloc.id);
        await fetchTaskAllocations(task.id);
        await refreshAllocated(alloc.sov_line_id);
      } catch (error) {
        console.error('Error removing link:', error);
      }
    };

    const inputStyle = {
      width: '100%', padding: '10px 14px', fontSize: '14px',
      border: `1px solid ${t.cardBorder}`, borderRadius: '10px', outline: 'none',
      transition: 'border-color 0.2s', backgroundColor: t.bg, boxSizing: 'border-box',
    };
    const labelStyle = { display: 'block', fontSize: '14px', fontWeight: 600, color: t.heading, marginBottom: '6px' };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: t.bg, borderRadius: '16px', width: '540px', maxHeight: '92vh',
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
            animation: 'slideUp 0.25s ease',
          }}
        >
          {/* Header */}
          <div style={{ padding: '24px 28px 16px', borderBottom: `1px solid ${t.tableBorder}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: t.heading }}>
                  SOV Links &mdash; {task.task_name}
                </h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: t.textMuted, lineHeight: 1.5 }}>
                  Link this task to one or more SOV line items. Progress updates will roll up to those lines.
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: t.textMuted, fontSize: '22px', lineHeight: 1, borderRadius: '6px',
                }}
                onMouseEnter={(e) => { e.target.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'; e.target.style.color = t.heading; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = t.textMuted; }}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
            {/* Existing links */}
            {allocations.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                {allocations.map(alloc => {
                  const contractAmt = parseFloat(alloc.sov_contract_amount) || 0;
                  const pctDisplay = contractAmt > 0 ? Math.round((parseFloat(alloc.allocation_amount) / contractAmt) * 100) : 0;
                  const methodLabel = alloc.allocation_method === 'Percentage'
                    ? `${pctDisplay}% Percent allocation`
                    : `$${(parseFloat(alloc.allocation_amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} Amount allocation`;

                  return (
                    <div key={alloc.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '14px 16px', backgroundColor: t.bgAlt, borderRadius: '10px',
                      marginBottom: '8px', border: `1px solid ${t.tableBorder}`,
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: t.heading }}>
                          {alloc.sov_line_description}
                        </div>
                        <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>
                          {methodLabel}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveLink(alloc)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                          color: '#fca5a5', fontSize: '18px', borderRadius: '6px', lineHeight: 1,
                        }}
                        onMouseEnter={(e) => { e.target.style.color = '#ef4444'; e.target.style.backgroundColor = '#fef2f2'; }}
                        onMouseLeave={(e) => { e.target.style.color = '#fca5a5'; e.target.style.backgroundColor = 'transparent'; }}
                        title="Remove link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {allocations.length === 0 && (
              <div style={{
                padding: '20px', textAlign: 'center', color: t.textMuted, fontSize: '13px',
                backgroundColor: t.bgAlt, borderRadius: '10px', marginBottom: '20px',
              }}>
                No SOV links yet. Add one below.
              </div>
            )}

            {/* Divider */}
            <div style={{
              fontSize: '12px', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: '16px', paddingBottom: '10px',
              borderBottom: `1px solid ${t.tableBorder}`,
            }}>
              Add Link
            </div>

            {/* SOV Line selector — searchable dropdown */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>SOV Line Item</label>
              <div style={{ position: 'relative' }} ref={sovInputRef}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowSovDropdown(true); setSovLineId(''); }}
                    onFocus={() => {
                      if (sovInputRef.current) {
                        const rect = sovInputRef.current.getBoundingClientRect();
                        setSovDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
                      }
                      setShowSovDropdown(true);
                    }}
                    onBlur={() => setTimeout(() => setShowSovDropdown(false), 200)}
                    placeholder="Search SOV lines..."
                    style={{ ...inputStyle, fontSize: '13px', paddingRight: '32px' }}
                  />
                  <svg style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showSovDropdown && (
                  <>
                    <div style={{
                      position: 'fixed', top: sovDropdownPos.top, left: sovDropdownPos.left, width: sovDropdownPos.width, zIndex: 99999,
                      backgroundColor: t.bg, border: `1px solid ${t.cardBorder}`, borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '350px', overflowY: 'auto',
                    }}>
                      {filteredSovLines.length === 0 ? (
                        <div style={{ padding: '12px 14px', fontSize: '12px', color: t.textMuted, textAlign: 'center' }}>
                          No matching SOV lines
                        </div>
                      ) : (
                        filteredSovLines.map(line => {
                          const contractAmt = parseFloat(line.contract_amount) || 0;
                          const totalAllocated = sovAllocatedMap[line.id] || 0;
                          const remaining = contractAmt - totalAllocated;
                          const pctAllocated = contractAmt > 0 ? Math.round((totalAllocated / contractAmt) * 100) : 0;
                          return (
                            <div key={line.id}
                              onClick={() => {
                                setSovLineId(String(line.id));
                                setSearchQuery(`${line.sov_code} - ${line.description}`);
                                setShowSovDropdown(false);
                                // Auto-set allocation value to remaining if current default would exceed
                                const lineContractAmt = parseFloat(line.contract_amount) || 0;
                                const lineTotalAllocated = sovAllocatedMap[line.id] || 0;
                                const lineRemainingPct = lineContractAmt > 0 ? Math.round(((lineContractAmt - lineTotalAllocated) / lineContractAmt) * 100) : 100;
                                if (allocationType === 'Percentage' && parseFloat(allocationValue || '100') > lineRemainingPct) {
                                  setAllocationValue(String(lineRemainingPct));
                                } else if (allocationType === 'Amount' && parseFloat(allocationValue || '0') > (lineContractAmt - lineTotalAllocated)) {
                                  setAllocationValue(String((lineContractAmt - lineTotalAllocated).toFixed(2)));
                                }
                              }}
                              style={{
                                padding: '10px 14px', fontSize: '13px', cursor: 'pointer',
                                borderBottom: `1px solid ${t.tableBorder}`,
                                transition: 'background-color 0.1s',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <div>
                                <span style={{ color: '#3b82f6', fontWeight: 600, marginRight: '8px' }}>{line.sov_code}</span>
                                <span style={{ color: t.text }}>{line.description}</span>
                              </div>
                              <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '2px' }}>
                                ${remaining.toLocaleString('en-US', { minimumFractionDigits: 0 })} remaining | {pctAllocated}% allocated
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Show selected SOV details card */}
            {sovLineId && (() => {
              const sel = sovLines.find(l => l.id === parseInt(sovLineId));
              if (!sel) return null;
              const contractAmt = parseFloat(sel.contract_amount) || 0;
              const totalAllocated = sovAllocatedMap[sel.id] || 0;
              const remaining = contractAmt - totalAllocated;
              const pctAllocated = contractAmt > 0 ? Math.round((totalAllocated / contractAmt) * 100) : 0;
              return (
                <div style={{
                  padding: '12px 16px', backgroundColor: '#eff6ff', borderRadius: '10px',
                  border: '1px solid #bfdbfe', marginBottom: '16px', fontSize: '12px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#1e40af', fontWeight: 600 }}>{sel.description}</span>
                    <span style={{ color: '#1e40af', fontWeight: 600 }}>${contractAmt.toLocaleString('en-US')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', color: t.textSecondary }}>
                    <span>Allocated: <strong style={{ color: t.text }}>${totalAllocated.toLocaleString('en-US')} ({pctAllocated}%)</strong></span>
                    <span>Remaining: <strong style={{ color: remaining > 0 ? '#16a34a' : '#dc2626' }}>${remaining.toLocaleString('en-US')}</strong></span>
                  </div>
                  {/* Visual bar */}
                  <div style={{ marginTop: '8px', height: '6px', backgroundColor: '#dbeafe', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(pctAllocated, 100)}%`, height: '100%',
                      backgroundColor: pctAllocated > 100 ? '#dc2626' : '#3b82f6', borderRadius: '3px',
                    }} />
                  </div>
                </div>
              );
            })()}

            {/* Allocation Type + Value */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Allocation Type</label>
                <select
                  value={allocationType}
                  onChange={(e) => {
                    setAllocationType(e.target.value);
                    setAllocationValue(e.target.value === 'Percentage' ? '100' : '');
                  }}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="Percentage">Percentage (%)</option>
                  <option value="Amount">Amount ($)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>{allocationType === 'Percentage' ? 'Percentage' : 'Amount'}</label>
                <input
                  type="number"
                  value={allocationValue}
                  onChange={(e) => setAllocationValue(e.target.value)}
                  placeholder={allocationType === 'Percentage' ? '100' : '0.00'}
                  min="0"
                  max={allocationType === 'Percentage' ? Math.round(selectedRemainingPct * 100) / 100 || 100 : selectedRemainingAmt || undefined}
                  step={allocationType === 'Percentage' ? '1' : '0.01'}
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = t.cardBorder}
                />
              </div>
            </div>

            {/* Preview what will be allocated */}
            {sovLineId && allocationValue && (() => {
              const sel = sovLines.find(l => l.id === parseInt(sovLineId));
              if (!sel) return null;
              const contractAmt = parseFloat(sel.contract_amount) || 0;
              let previewAmount = 0;
              if (allocationType === 'Percentage') {
                previewAmount = (contractAmt * parseFloat(allocationValue)) / 100;
              } else {
                previewAmount = parseFloat(allocationValue);
              }
              return (
                <div style={{ marginBottom: '16px', padding: '0 2px' }}>
                  <div style={{ fontSize: '12px', color: t.textSecondary }}>
                    This will allocate <strong style={{ color: t.heading }}>${previewAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong> from {sel.sov_code} to this task.
                  </div>
                  {wouldExceed && (
                    <div style={{
                      fontSize: '12px', color: '#dc2626', fontWeight: 600, marginTop: '6px',
                      padding: '8px 12px', backgroundColor: '#fef2f2', borderRadius: '8px',
                      border: '1px solid #fecaca',
                    }}>
                      Exceeds remaining capacity. Only {Math.round(selectedRemainingPct)}% (${selectedRemainingAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}) is available.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Add Link button */}
            <button
              onClick={handleAddLink}
              disabled={!sovLineId || !allocationValue || wouldExceed}
              style={{
                width: '100%', padding: '12px', fontSize: '14px', fontWeight: 600,
                border: 'none', borderRadius: '10px', cursor: !sovLineId || !allocationValue || wouldExceed ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                backgroundColor: !sovLineId || !allocationValue || wouldExceed ? t.cardBorder : '#93c5fd',
                color: !sovLineId || !allocationValue || wouldExceed ? t.textMuted : '#1e3a5f',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (sovLineId && allocationValue && !wouldExceed) e.target.style.backgroundColor = '#7cb8fc'; }}
              onMouseLeave={(e) => { if (sovLineId && allocationValue && !wouldExceed) e.target.style.backgroundColor = '#93c5fd'; }}
            >
              <span style={{ fontSize: '16px' }}>+</span> Add Link
            </button>
          </div>

          {/* Footer */}
          <div style={{ padding: '16px 28px 20px', borderTop: `1px solid ${t.tableBorder}`, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 28px', fontSize: '14px', fontWeight: 500, border: `1px solid ${t.cardBorder}`,
                borderRadius: '10px', backgroundColor: t.bg, color: t.text, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = t.bgAlt; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = t.bg; }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============ GROUP & SORT TASKS ============
  const groupedTasks = tasks.reduce((acc, task) => {
    const section = task.building_area_phase || 'Unsectioned';
    if (!acc[section]) acc[section] = [];
    acc[section].push(task);
    return acc;
  }, {});

  // Sort tasks within each section by task_code
  Object.keys(groupedTasks).forEach(section => {
    groupedTasks[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
  });

  // Merge task-based sections with persisted sections, respecting saved sort_order
  const savedSectionNames = savedSections.map(s => s.section_name);
  const allSectionNames = new Set([...savedSectionNames, ...Object.keys(groupedTasks)]);
  // Order: saved sections first (by sort_order), then any new unsaved sections alphabetically
  const savedSet = new Set(savedSectionNames);
  const unsavedNames = Array.from(allSectionNames).filter(n => !savedSet.has(n)).sort();
  const sections = [...savedSectionNames.filter(n => allSectionNames.has(n)), ...unsavedNames];
  sectionsRef.current = sections;

  // Auto-persist section order when there are unsaved sections (from tasks not yet in sections table)
  useEffect(() => {
    if (sectionsLoaded && unsavedNames.length > 0 && sections.length > 0 && projectId) {
      reorderSections(projectId, sections).then(updated => {
        setSavedSections(updated);
      }).catch(err => console.error('Error auto-saving section order:', err));
    }
  }, [sectionsLoaded, unsavedNames.length, projectId]); // eslint-disable-line

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ============ PERCENT COMPLETE INLINE EDIT ============
  const handlePercentCompleteDoubleClick = (taskId, currentValue) => {
    setEditingPercentCompleteId(taskId);
    setEditingPercentCompleteValue(currentValue || 0);
  };

  const handlePercentCompleteEditSave = (taskId) => {
    const val = editingPercentCompleteValue;
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
    // Fire the update without awaiting — optimistic local state handles the UI instantly
    handlePercentCompleteChange(taskId, val);
  };

  const handlePercentCompleteEditCancel = () => {
    setEditingPercentCompleteId(null);
    setEditingPercentCompleteValue('');
  };

  // ============ XLSX TEMPLATE & IMPORT/EXPORT ============
  const handleDownloadTaskTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = ['task_code', 'task_name', 'task_description', 'building_area_phase', 'task_type', 'start_date', 'finish_date', 'responsible_party', 'client_visible', 'milestone_flag', 'notes'];
    const sampleRows = [
      ['HS-001', 'Material Delivery', 'Deliver materials to site', 'High School', 'Procurement', '2026-04-01', '2026-04-05', 'John Smith', 'true', 'false', 'Example task'],
      ['HS-002', 'Demo Old Unit', 'Remove existing equipment', 'High School', 'Labor', '2026-04-06', '2026-04-10', 'Jane Doe', 'true', 'false', ''],
      ['HS-003', 'Install New Unit', 'Install and connect', 'High School', 'Labor', '2026-04-11', '2026-04-20', 'John Smith', 'true', 'false', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
    ws['!cols'] = [
      { wch: 12 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 },
      { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 25 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Task Data');
    const legendData = [
      ['FIELD', 'VALID OPTIONS / FORMAT'], [''],
      ['task_type', 'Labor, Material, Procurement, Engineering, Inspection, Milestone'],
      ['start_date / finish_date', 'YYYY-MM-DD format (e.g., 2026-04-01)'],
      ['client_visible', 'true or false'],
      ['milestone_flag', 'true or false'],
      ['building_area_phase', 'Groups tasks into sections (e.g., High School, Junior High)'],
    ];
    const wsLegend = XLSX.utils.aoa_to_sheet(legendData);
    wsLegend['!cols'] = [{ wch: 24 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, wsLegend, 'Field Guide');
    XLSX.writeFile(wb, 'tasks_template.xlsx');
  };

  const parseTaskFileRows = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'xlsx' || ext === 'xls' || ext === 'xlsm') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    } else {
      const text = await file.text();
      const lines = text.split('\n').map(line => {
        const result = []; let current = ''; let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
          else current += char;
        }
        result.push(current.trim());
        return result;
      });
      if (lines.length < 2) return [];
      const headers = lines[0].map(h => h.toLowerCase().trim());
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row || row.length < 2 || !row[0] || row[0].startsWith('---')) continue;
        const rowData = {};
        headers.forEach((header, idx) => { if (idx < row.length) rowData[header] = row[idx]; });
        rows.push(rowData);
      }
      return rows;
    }
  };

  const normalizeDate = (val) => {
    if (!val) return '';
    if (val instanceof Date) {
      return `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`;
    }
    const str = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const match = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) return `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
    return str;
  };

  const handleImportTaskFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setLoading(true);
      const rows = await parseTaskFileRows(file);
      if (rows.length === 0) { alert('File must have headers and at least one data row'); return; }
      let importCount = 0;
      for (const rowData of rows) {
        const row = {};
        Object.keys(rowData).forEach(k => { row[k.toLowerCase().trim()] = rowData[k]; });
        if (row.task_name || row.task_code) {
          const startDate = normalizeDate(row.start_date);
          const finishDate = normalizeDate(row.finish_date);
          let duration = 0;
          if (startDate && finishDate) {
            duration = Math.max(1, Math.ceil((new Date(finishDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1);
          }
          const cv = String(row.client_visible || 'true').toLowerCase();
          const mf = String(row.milestone_flag || 'false').toLowerCase();
          await createTask({
            project_id: projectId,
            task_code: String(row.task_code || ''),
            task_name: String(row.task_name || ''),
            task_description: String(row.task_description || ''),
            building_area_phase: String(row.building_area_phase || ''),
            task_type: String(row.task_type || 'Work'),
            start_date: startDate, finish_date: finishDate, duration_days: duration,
            responsible_party: String(row.responsible_party || ''),
            client_visible: (cv === 'true' || cv === '1') ? 1 : 0,
            milestone_flag: (mf === 'true' || mf === '1') ? 1 : 0,
            notes: String(row.notes || ''),
          });
          importCount++;
        }
      }
      await fetchTasks();
      alert(`Successfully imported ${importCount} task(s)`);
    } catch (err) {
      alert('Failed to import file: ' + err.message);
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleExportTasks = () => {
    const wb = XLSX.utils.book_new();
    const exportData = tasks.map(t => ({
      task_code: t.task_code, task_name: t.task_name, task_description: t.task_description,
      building_area_phase: t.building_area_phase, task_type: t.task_type,
      start_date: t.start_date, finish_date: t.finish_date, duration_days: t.duration_days,
      percent_complete: t.percent_complete || 0, status: t.status || 'Not Started',
      responsible_party: t.responsible_party,
      client_visible: t.client_visible ? 'true' : 'false',
      milestone_flag: t.milestone_flag ? 'true' : 'false',
      notes: t.notes,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, 'tasks_export.xlsx');
  };

  // ============ SECTION & TASK ACTIONS ============
  const handleAddSection = () => {
    setNewSectionName('');
    setSectionNameError('');
    setShowAddSectionModal(true);
  };

  const handleConfirmAddSection = async () => {
    const trimmed = (newSectionName || '').trim();
    if (!trimmed) {
      setSectionNameError('Section name is required.');
      return;
    }
    const existingSections = getExistingSections();
    const exactMatch = existingSections.find(s => s === trimmed);
    if (exactMatch) {
      setSectionNameError(`A section named "${exactMatch}" already exists.`);
      return;
    }
    const caseMatch = existingSections.find(s => s.toLowerCase() === trimmed.toLowerCase());
    if (caseMatch) {
      setSectionNameError(`A section named "${caseMatch}" already exists (names are case-insensitive).`);
      return;
    }
    try {
      await createSection({ project_id: projectId, section_name: trimmed });
      await fetchSections();
      setShowAddSectionModal(false);
      setNewSectionName('');
      setSectionNameError('');
    } catch (error) {
      console.error('Error creating section:', error);
      setSectionNameError('Failed to create section. Please try again.');
    }
  };

  const handleAddTaskToSection = (section) => {
    setFormData({
      task_code: '', task_name: '', task_description: '', building_area_phase: section,
      task_type: 'Labor', start_date: '', finish_date: '', duration_days: 0,
      responsible_party: '', client_visible: true, milestone_flag: false, notes: ''
    });
    setShowModal(true);
  };

  // ============ EDIT (PROPERTIES) MODAL ============
  const handleEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditFormData({
      task_code: task.task_code || '', task_name: task.task_name || '',
      task_description: task.task_description || '', building_area_phase: task.building_area_phase || '',
      task_type: task.task_type || 'Labor', start_date: task.start_date || '',
      finish_date: task.finish_date || '', duration_days: task.duration_days || 0,
      responsible_party: task.responsible_party || '', status: task.status || 'Not Started',
      client_visible: !!task.client_visible, milestone_flag: !!task.milestone_flag,
      notes: task.notes || '',
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newData = { [name]: type === 'checkbox' ? checked : value };
    if (name === 'start_date' && value && !editFormData.finish_date) newData.finish_date = value;
    setEditFormData(prev => ({ ...prev, ...newData }));
  };

  const editDuration = (() => {
    if (editFormData.start_date && editFormData.finish_date) {
      const s = new Date(editFormData.start_date);
      const f = new Date(editFormData.finish_date);
      return Math.max(0, Math.ceil((f - s) / (1000 * 60 * 60 * 24)) + 1);
    }
    return editFormData.duration_days || 0;
  })();

  const handleSaveEdit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    console.log('handleSaveEdit called', { editingTaskId, editFormData });
    // Validate section name — prevent near-duplicate names (e.g., "elementary school" vs "Elementary School")
    const originalTask = tasks.find(t => t.id === editingTaskId);
    const newSection = (editFormData.building_area_phase || '').trim();
    const originalSection = originalTask ? (originalTask.building_area_phase || '').trim() : '';
    if (newSection && newSection !== originalSection) {
      const existingSections = getExistingSections();
      // Check if the exact name already exists (this is fine — it just moves the task into that section)
      const exactMatch = existingSections.find(s => s === newSection);
      if (!exactMatch) {
        // No exact match — check for case-insensitive match which would create a confusing near-duplicate
        const caseMatch = existingSections.find(s => s.toLowerCase() === newSection.toLowerCase());
        if (caseMatch) {
          alert(`A section named "${caseMatch}" already exists. Did you mean to use that exact name? Section names must be unique (case-insensitive).`);
          return;
        }
      }
    }
    // Enforce: if task is scheduled entirely in the future, completion must be 0%
    // This protects billing integrity — can't bill for work on days that haven't happened
    const today = new Date().toISOString().split('T')[0];
    const newStart = editFormData.start_date;
    const newEnd = editFormData.finish_date;
    const origTask = tasks.find(tk => tk.id === editingTaskId);
    const pct = parseFloat(origTask?.percent_complete) || 0;
    const bothDatesInFuture = newStart && newEnd && newStart > today && newEnd > today;
    console.log('[SaveEdit] Date check:', { today, newStart, newEnd, pct, bothDatesInFuture, origPct: origTask?.percent_complete });

    // Proration guard: cap progress at what the schedule allows as of today
    const maxPctForEdit = getMaxProgress({ start_date: newStart, finish_date: newEnd });
    if (pct > maxPctForEdit) {
      const cappedPct = maxPctForEdit;
      if (cappedPct === 0) {
        await confirm({
          title: 'Completion Reset Required',
          message: `This task has ${pct}% completion but is now scheduled entirely in the future (starts ${newStart}). Completion will be reset to 0% because work cannot be billed for dates that haven't occurred yet.`,
          confirmLabel: 'OK',
          danger: false,
        });
      } else {
        await confirm({
          title: 'Progress Will Be Capped',
          message: `This task is at ${pct}% but the schedule (${newStart} – ${newEnd}) only supports ${cappedPct}% based on today's date. Progress will be reduced to ${cappedPct}%.`,
          confirmLabel: 'OK',
          danger: false,
        });
      }
    }

    try {
      const saveData = {
        ...editFormData,
        duration_days: editDuration,
        client_visible: editFormData.client_visible ? 1 : 0,
        milestone_flag: editFormData.milestone_flag ? 1 : 0,
      };
      // Enforce proration: cap percent_complete at the schedule-prorated max
      if (pct > maxPctForEdit) {
        saveData.percent_complete = maxPctForEdit;
        saveData.status = maxPctForEdit === 0 ? 'Not Started' : maxPctForEdit >= 100 ? 'Complete' : 'In Progress';
      }
      console.log('[SaveEdit] Saving:', { percent_complete: saveData.percent_complete, status: saveData.status, start: saveData.start_date, end: saveData.finish_date });
      await updateTask(editingTaskId, saveData);
      setEditingTaskId(null);
      setEditFormData({});
      fetchTasks({ preserveScroll: true });
      refreshSummary();
    } catch (err) {
      console.error('Error updating task:', err);
      alert('Failed to save: ' + err.message);
    }
  };

  // Get SOV link names for display
  const getSOVLinkNames = (task) => {
    const allocs = taskAllocations[task.id];
    if (allocs && allocs.length > 0) {
      return allocs.map(a => a.sov_line_description || a.sov_code).join(', ');
    }
    return '';
  };

  // ============ RENDER ============
  const gridCols = '30px 80px 1fr 100px 180px 100px 120px 150px 40px 40px';

  return (
    <div className="tasks-tab">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: t.heading }}>Construction Schedule Tasks</h2>
          <span style={{ color: t.textMuted, fontSize: '13px' }}>{tasks.length} tasks</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-secondary" onClick={handleDownloadTaskTemplate} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#8681;</span> Template
          </button>
          <button className="btn btn-secondary" onClick={handleExportTasks} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#9113;</span> Export Tasks
          </button>
          <label className="btn btn-secondary" style={{ cursor: 'pointer', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#8679;</span> Import Excel
            <input type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={handleImportTaskFile} style={{ display: 'none' }} />
          </label>
          <button className="btn btn-secondary" onClick={handleAddSection} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px' }}>&#9776;</span> Add Section
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            + Add Task
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading tasks...</p>
      ) : (
        <div>
          {/* Column headers */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols,
            padding: '10px 12px', borderBottom: `2px solid ${t.cardBorder}`,
            fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
            color: t.textSecondary, letterSpacing: '0.5px',
          }}>
            <div></div>
            <div>Code</div>
            <div>Task</div>
            <div>Type</div>
            <div>Dates</div>
            <div>Status</div>
            <div>% Done</div>
            <div>SOV Links</div>
            <div></div>
            <div></div>
          </div>

          {sections.map(section => (
            <div
              key={section}
              onDragOver={(e) => { if (draggedSectionName) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverSectionName(section); } }}
              onDrop={(e) => { if (draggedSectionName) handleSectionDrop(e, section); }}
              style={{
                borderTop: dragOverSectionName === section && draggedSectionName && draggedSectionName !== section ? '3px solid #3b82f6' : '3px solid transparent',
                transition: 'border-top 0.15s',
              }}
            >
              {/* Section header */}
              <div
                draggable
                onDragStart={(e) => handleSectionDragStart(e, section)}
                onDragEnd={handleSectionDragEnd}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 12px',
                  backgroundColor: dragOverSectionName === section && draggedSectionName && draggedSectionName !== section ? '#dbeafe' : t.bgAlt,
                  borderBottom: `1px solid ${t.cardBorder}`, cursor: 'grab',
                  transition: 'background-color 0.15s',
                }}
                onClick={() => { if (!draggedSectionName) toggleSection(section); }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: t.textMuted, cursor: 'grab', userSelect: 'none' }} title="Drag to reorder section">⠿</span>
                  <span style={{ fontSize: '12px', color: t.textSecondary }}>
                    {collapsedSections[section] ? '\u25B6' : '\u25BC'}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: t.heading }}>
                    {section}
                  </span>
                  <span style={{ color: t.textMuted, fontSize: '12px', fontWeight: 400 }}>
                    {(groupedTasks[section] || []).length} tasks
                  </span>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={(e) => { e.stopPropagation(); handleAddTaskToSection(section); }}
                  style={{ fontSize: '12px', padding: '4px 12px' }}
                >
                  + Add Task
                </button>
              </div>

              {/* Empty section placeholder */}
              {!collapsedSections[section] && (!groupedTasks[section] || groupedTasks[section].length === 0) && (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: t.textMuted, fontSize: '13px', borderBottom: `1px solid ${t.tableBorder}` }}>
                  No tasks yet — click "+ Add Task" above to get started
                </div>
              )}
              {/* Task rows */}
              {!collapsedSections[section] && (groupedTasks[section] || []).map((task, idx) => {
                const pct = task.percent_complete || 0;
                const hasAllocations = taskAllocations[task.id] && taskAllocations[task.id].length > 0;
                const duration = task.duration_days || 0;
                const validPredecessors = (task.predecessors || []).filter(p => p.predecessor_task_id != null);
                const hasPredecessors = validPredecessors.length > 0;
                const successorTasks = tasks.filter(t => (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(task.id)));
                const isPrereqForOthers = successorTasks.length > 0;
                const isDragOver = dragOverTask === task.id;
                const depFlagged = (depAlertData.flagged_task_ids || []).includes(task.id);
                const taskDepAlerts = depFlagged ? (depAlertData.alerts || []).filter(a => a.task_id === task.id || a.related_task_id === task.id) : [];

                return (
                  <React.Fragment key={task.id}>
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, task, section)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, task)}
                      onDrop={(e) => handleDrop(e, task, section)}
                      onDoubleClick={() => handleEditTask(task)}
                      style={{
                        display: 'grid', gridTemplateColumns: gridCols,
                        padding: '12px 12px', alignItems: 'center', fontSize: '13px',
                        borderBottom: `1px solid ${t.tableBorder}`,
                        borderTop: isDragOver ? '2px solid #3b82f6' : 'none',
                        borderLeft: depFlagged ? (taskDepAlerts.some(a => a.severity === 'critical') ? '3px solid #dc2626' : '3px solid #f59e0b') : flaggedTaskIds.has(task.id) ? '3px solid #f59e0b' : '3px solid transparent',
                        backgroundColor: depFlagged ? (taskDepAlerts.some(a => a.severity === 'critical') ? '#fef2f2' : '#fffbeb') : flaggedTaskIds.has(task.id) ? '#fffbeb' : 'transparent',
                        transition: 'background-color 0.15s',
                        cursor: 'default',
                        userSelect: 'none',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = (depFlagged || flaggedTaskIds.has(task.id)) ? '#fef3c7' : (t.dark ? 'rgba(255,255,255,0.03)' : '#f8fafc')}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = depFlagged ? (taskDepAlerts.some(a => a.severity === 'critical') ? '#fef2f2' : '#fffbeb') : flaggedTaskIds.has(task.id) ? '#fffbeb' : 'transparent'}
                    >
                      {/* Drag handle - 6 dot grip */}
                      <div style={{ cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px 0' }}>
                        <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
                          <circle cx="3" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="2" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="8" r="1.3" fill="#cbd5e1" />
                          <circle cx="3" cy="14" r="1.3" fill="#cbd5e1" />
                          <circle cx="7" cy="14" r="1.3" fill="#cbd5e1" />
                        </svg>
                      </div>

                      {/* Code */}
                      <div style={{ color: t.textSecondary, fontWeight: 500, fontSize: '12px' }}>{task.task_code}</div>

                      {/* Task name + link indicators */}
                      <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', color: t.heading }}>
                        {task.task_name}
                        {depFlagged && (
                          <span
                            title={taskDepAlerts.map(a => a.message).join('\n') || 'Scheduling alert'}
                            style={{
                              fontSize: '13px',
                              cursor: 'help',
                              color: taskDepAlerts.some(a => a.severity === 'critical') ? '#dc2626' : '#d97706',
                            }}
                          >&#9888;</span>
                        )}
                        {isPrereqForOthers && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#c4b5fd', marginBottom: successorTasks.length > 1 ? '4px' : '0' }}>Dependents:</div>
                              {successorTasks.map(s => (
                                <div key={s.id} style={{ padding: '1px 0' }}>
                                  <span style={{ color: '#c4b5fd', fontWeight: 600 }}>{s.task_code}</span>
                                  <span style={{ color: t.cardBorder }}> {s.task_name}</span>
                                </div>
                              ))}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {hasPredecessors && (
                          <span className="dep-tooltip-wrap" style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer' }}>
                              <line x1="19" y1="12" x2="5" y2="12" />
                              <polyline points="12 19 5 12 12 5" />
                            </svg>
                            <div className="dep-tooltip" style={{
                              display: 'none', position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                              marginBottom: '6px', padding: '8px 12px', backgroundColor: '#1e293b', color: '#f8fafc',
                              borderRadius: '8px', fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
                            }}>
                              <div style={{ fontWeight: 600, color: '#6ee7b7', marginBottom: validPredecessors.length > 1 ? '4px' : '0' }}>Predecessors:</div>
                              {validPredecessors.map(p => {
                                const pt = tasks.find(t => Number(t.id) === Number(p.predecessor_task_id));
                                return (
                                  <div key={p.id || p.predecessor_task_id} style={{ padding: '1px 0' }}>
                                    <span style={{ color: '#6ee7b7', fontWeight: 600 }}>{pt ? pt.task_code : '?'}</span>
                                    <span style={{ color: '#e2e8f0' }}> {pt ? pt.task_name : 'Unknown'}</span>
                                  </div>
                                );
                              })}
                              <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                                width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid #1e293b' }} />
                            </div>
                          </span>
                        )}
                        {hasAllocations && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        )}
                      </div>

                      {/* Type badge */}
                      <div>
                        <span style={{
                          ...getTaskTypeStyle(task.task_type),
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                          fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap',
                        }}>
                          {task.task_type}
                        </span>
                      </div>

                      {/* Dates + duration */}
                      <div>
                        <div style={{ fontSize: '13px', color: t.text }}>{fmtDate(task.start_date)} &rarr; {fmtDate(task.finish_date)}</div>
                        <div style={{ fontSize: '11px', color: t.textMuted }}>{duration}d</div>
                      </div>

                      {/* Status badge */}
                      <div>
                        <span style={{
                          ...getStatusStyle(task.status),
                          padding: '3px 8px', borderRadius: '10px', fontSize: '11px',
                          fontWeight: 500, display: 'inline-block', whiteSpace: 'nowrap',
                        }}>
                          {task.status || 'Not Started'}
                        </span>
                      </div>

                      {/* % Done with progress bar */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handlePercentCompleteDoubleClick(task.id, pct);
                        }}
                      >
                        {editingPercentCompleteId === task.id ? (
                          <input
                            type="number" min="0" max="100"
                            value={editingPercentCompleteValue}
                            onChange={(e) => setEditingPercentCompleteValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handlePercentCompleteEditSave(task.id);
                              else if (e.key === 'Escape') handlePercentCompleteEditCancel();
                            }}
                            onBlur={() => handlePercentCompleteEditSave(task.id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '50px', fontSize: '12px', padding: '2px 4px', border: '1px solid #3b82f6', borderRadius: '4px', outline: 'none' }}
                          />
                        ) : (
                          <>
                            <div style={{
                              width: '50px', height: '6px', backgroundColor: t.cardBorder,
                              borderRadius: '3px', overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${pct}%`, height: '100%',
                                backgroundColor: pct >= 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#94a3b8',
                                borderRadius: '3px', transition: 'width 0.3s',
                              }} />
                            </div>
                            <span style={{ fontSize: '12px', color: t.textSecondary, fontWeight: 500 }}>{pct}%</span>
                          </>
                        )}
                      </div>

                      {/* SOV Links text */}
                      <div style={{ fontSize: '12px', color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getSOVLinkNames(task)}
                      </div>

                      {/* Link icon - opens SOV linking panel */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          if (linkingTaskId === task.id) {
                            setLinkingTaskId(null);
                          } else {
                            setLinkingTaskId(task.id);
                            fetchTaskAllocations(task.id);
                          }
                        }}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                        title="Link to SOV"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={linkingTaskId === task.id ? '#3b82f6' : t.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      </div>

                      {/* Edit (pencil) icon - sleek B&W */}
                      <div
                        onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                        style={{ textAlign: 'center', cursor: 'pointer' }}
                        title="Edit Task Properties"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={t.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                        </svg>
                      </div>
                    </div>

                    {/* SOV Link modal rendered at bottom of component */}
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Add Section Modal */}
      {showAddSectionModal && (
        <div className="modal-overlay" onClick={() => setShowAddSectionModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: t.bg, borderRadius: '12px', width: '420px', maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'slideUp 0.2s ease',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '18px 24px', borderBottom: `1px solid ${t.cardBorder}`,
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Add New Section</h3>
              <button
                onClick={() => setShowAddSectionModal(false)}
                style={{
                  background: 'none', border: 'none', fontSize: '20px', color: t.textMuted,
                  cursor: 'pointer', padding: '0 4px', lineHeight: 1,
                }}
              >&times;</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: t.text, marginBottom: '6px' }}>
                Section Name (Building Area / Phase)
              </label>
              <input
                type="text"
                autoFocus
                value={newSectionName}
                onChange={(e) => { setNewSectionName(e.target.value); setSectionNameError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirmAddSection(); } }}
                placeholder="e.g. Elementary School, High School Wing B"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: '14px',
                  border: sectionNameError ? '1px solid #ef4444' : `1px solid ${t.cardBorder}`,
                  borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { if (!sectionNameError) e.target.style.borderColor = '#3b82f6'; }}
                onBlur={(e) => { if (!sectionNameError) e.target.style.borderColor = t.cardBorder; }}
              />
              {sectionNameError && (
                <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>{sectionNameError}</div>
              )}
            </div>
            {/* Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '10px',
              padding: '16px 24px', borderTop: `1px solid ${t.tableBorder}`,
            }}>
              <button
                onClick={() => setShowAddSectionModal(false)}
                style={{
                  padding: '8px 20px', fontSize: '13px', fontWeight: 500,
                  border: `1px solid ${t.cardBorder}`, borderRadius: '8px', background: t.bg,
                  color: t.text, cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleConfirmAddSection}
                style={{
                  padding: '8px 20px', fontSize: '13px', fontWeight: 600,
                  border: 'none', borderRadius: '8px', background: '#3b82f6',
                  color: '#fff', cursor: 'pointer',
                }}
              >Create Section</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showModal && (() => {
        const atInputStyle = {
          width: '100%', padding: '10px 12px', fontSize: '14px',
          border: `1px solid ${t.cardBorder}`, borderRadius: '8px', outline: 'none',
          boxSizing: 'border-box', transition: 'border-color 0.15s', backgroundColor: t.bg,
        };
        const atLabelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: t.text, marginBottom: '5px' };
        const atFieldGroup = { marginBottom: '16px' };
        const handleFocus = (e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; };
        const handleBlur = (e) => { e.target.style.borderColor = t.cardBorder; e.target.style.boxShadow = 'none'; };
        return (
          <div className="modal-overlay" onClick={() => { setShowModal(false); setFormErrors({}); setPendingPredecessors([]); }}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: t.bg, borderRadius: '12px', width: '520px', maxWidth: '90vw',
                maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'slideUp 0.2s ease',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '18px 24px', borderBottom: `1px solid ${t.cardBorder}`, flexShrink: 0,
              }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Add New Task</h3>
                <button
                  onClick={() => { setShowModal(false); setFormErrors({}); setPendingPredecessors([]); }}
                  style={{
                    background: 'none', border: 'none', fontSize: '22px', color: t.textMuted,
                    cursor: 'pointer', padding: '0 4px', lineHeight: 1,
                  }}
                >&times;</button>
              </div>
              {/* Body */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
                  {/* Task Name */}
                  <div style={atFieldGroup}>
                    <label style={atLabelStyle}>Task Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" name="task_name" value={formData.task_name} onChange={handleFormChange}
                      style={{ ...atInputStyle, borderColor: formErrors.task_name ? '#ef4444' : t.cardBorder }}
                      onFocus={handleFocus} onBlur={handleBlur}
                      placeholder="Enter task name" />
                    {formErrors.task_name && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{formErrors.task_name}</div>}
                  </div>
                  {/* Description */}
                  <div style={atFieldGroup}>
                    <label style={atLabelStyle}>Description</label>
                    <textarea name="task_description" value={formData.task_description} onChange={handleFormChange}
                      rows="2" placeholder="Optional description"
                      style={{ ...atInputStyle, resize: 'vertical', minHeight: '60px' }}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                  {/* Section + Type row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', ...atFieldGroup }}>
                    <div>
                      <label style={atLabelStyle}>Section <span style={{ color: '#ef4444' }}>*</span></label>
                      {formData._addingNewSection ? (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="New section name..."
                            value={formData._newSectionName || ''}
                            autoFocus
                            onChange={e => setFormData(f => ({ ...f, _newSectionName: e.target.value }))}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const name = (formData._newSectionName || '').trim();
                                if (name) {
                                  createSection({ project_id: projectId, section_name: name })
                                    .then(() => fetchSections())
                                    .catch(() => {});
                                  setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                                }
                              } else if (e.key === 'Escape') {
                                setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }));
                              }
                            }}
                            style={{ ...atInputStyle, flex: 1, borderColor: formErrors.building_area_phase ? '#ef4444' : t.cardBorder }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const name = (formData._newSectionName || '').trim();
                              if (name) {
                                createSection({ project_id: projectId, section_name: name })
                                  .then(() => fetchSections())
                                  .catch(() => {});
                                setFormData(f => ({ ...f, building_area_phase: name, _addingNewSection: false, _newSectionName: '' }));
                              }
                            }}
                            style={{
                              padding: '6px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                              border: `1px solid #3b82f6`, borderRadius: '6px', background: '#3b82f6', color: '#fff',
                              whiteSpace: 'nowrap',
                            }}
                          >Add</button>
                          <button
                            type="button"
                            onClick={() => setFormData(f => ({ ...f, _addingNewSection: false, _newSectionName: '' }))}
                            style={{
                              padding: '6px 8px', fontSize: '12px', cursor: 'pointer',
                              border: `1px solid ${t.cardBorder}`, borderRadius: '6px', background: 'transparent', color: t.textMuted,
                            }}
                          >Cancel</button>
                        </div>
                      ) : (
                        <select name="building_area_phase" value={formData.building_area_phase} onChange={e => {
                            if (e.target.value === '__new__') {
                              setFormData(f => ({ ...f, _addingNewSection: true, _newSectionName: '' }));
                            } else {
                              handleFormChange(e);
                            }
                          }}
                          style={{ ...atInputStyle, cursor: 'pointer', borderColor: formErrors.building_area_phase ? '#ef4444' : t.cardBorder }}
                          onFocus={handleFocus} onBlur={handleBlur}>
                          <option value="">— Select Section —</option>
                          {getExistingSections().sort().map(sec => (
                            <option key={sec} value={sec}>{sec}</option>
                          ))}
                          <option value="__new__" style={{ fontWeight: 600 }}>+ Add New Section</option>
                        </select>
                      )}
                      {formErrors.building_area_phase && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{formErrors.building_area_phase}</div>}
                    </div>
                    <div>
                      <label style={atLabelStyle}>Task Type</label>
                      <select name="task_type" value={formData.task_type} onChange={handleFormChange}
                        style={{ ...atInputStyle, cursor: 'pointer' }} onFocus={handleFocus} onBlur={handleBlur}>
                        <option>Labor</option>
                        <option>Material</option>
                        <option>Procurement</option>
                        <option>Engineering</option>
                        <option>Inspection</option>
                        <option>Milestone</option>
                      </select>
                    </div>
                  </div>
                  {/* Dates row */}
                  {(() => {
                    // Compute earliest allowed dates from pending predecessors
                    let earliestStart = null;
                    let earliestFinish = null;
                    let startHint = '';
                    let finishHint = '';
                    const addDays = (dateStr, days) => {
                      const d = new Date(dateStr + 'T00:00:00');
                      d.setDate(d.getDate() + days);
                      return d;
                    };
                    const fmtISO = (d) => d.toISOString().split('T')[0];
                    const fmtDisplay = (d) => d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

                    for (const pred of pendingPredecessors) {
                      const pt = tasks.find(t => Number(t.id) === Number(pred.predecessor_task_id));
                      if (!pt) continue;
                      const lag = pred.lag_days || 0;
                      switch (pred.dependency_type) {
                        case 'FS':
                          if (pt.finish_date) {
                            const d = addDays(pt.finish_date, lag);
                            if (!earliestStart || d > earliestStart) { earliestStart = d; startHint = `Earliest: ${fmtDisplay(d)} (after ${pt.task_code} finishes)`; }
                          }
                          break;
                        case 'SS':
                          if (pt.start_date) {
                            const d = addDays(pt.start_date, lag);
                            if (!earliestStart || d > earliestStart) { earliestStart = d; startHint = `Earliest: ${fmtDisplay(d)} (with ${pt.task_code} start)`; }
                          }
                          break;
                        case 'FF':
                          if (pt.finish_date) {
                            const d = addDays(pt.finish_date, lag);
                            if (!earliestFinish || d > earliestFinish) { earliestFinish = d; finishHint = `Earliest: ${fmtDisplay(d)} (with ${pt.task_code} finish)`; }
                          }
                          break;
                        case 'SF':
                          if (pt.start_date) {
                            const d = addDays(pt.start_date, lag);
                            if (!earliestFinish || d > earliestFinish) { earliestFinish = d; finishHint = `Earliest: ${fmtDisplay(d)} (after ${pt.task_code} starts)`; }
                          }
                          break;
                        default: break;
                      }
                    }
                    const startMin = earliestStart ? fmtISO(earliestStart) : undefined;
                    const finishMinFromPred = earliestFinish ? fmtISO(earliestFinish) : undefined;
                    const finishMin = [formData.start_date, finishMinFromPred].filter(Boolean).sort().pop() || undefined;

                    return (
                      <div style={atFieldGroup}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                          <div>
                            <label style={atLabelStyle}>Start Date <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="date" name="start_date" value={formData.start_date} onChange={handleFormChange}
                              min={startMin}
                              style={{ ...atInputStyle, borderColor: formErrors.start_date ? '#ef4444' : t.cardBorder }}
                              onFocus={handleFocus} onBlur={handleBlur} />
                            {formErrors.start_date && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{formErrors.start_date}</div>}
                          </div>
                          <div>
                            <label style={atLabelStyle}>Finish Date <span style={{ color: '#ef4444' }}>*</span></label>
                            <input type="date" name="finish_date" value={formData.finish_date} onChange={handleFormChange}
                              min={finishMin}
                              style={{ ...atInputStyle, borderColor: formErrors.finish_date ? '#ef4444' : t.cardBorder }}
                              onFocus={handleFocus} onBlur={handleBlur} />
                            {formErrors.finish_date && <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '4px' }}>{formErrors.finish_date}</div>}
                          </div>
                          <div>
                            <label style={atLabelStyle}>Duration (Days)</label>
                            <input type="number" name="duration_days" value={formData.duration_days} disabled
                              style={{ ...atInputStyle, backgroundColor: t.bgAlt, color: t.textSecondary, cursor: 'default' }} />
                          </div>
                        </div>
                        {(startHint || finishHint) && (
                          <div style={{ color: '#3b82f6', fontSize: '11px', marginTop: '6px', whiteSpace: 'nowrap' }}>
                            {startHint || finishHint}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Predecessors */}
                  <div style={atFieldGroup}>
                    <label style={atLabelStyle}>Predecessors</label>
                    <PredecessorSection
                      currentTask={null}
                      tasks={tasks}
                      editingTaskId={null}
                      inputStyle={atInputStyle}
                      fieldGroup={{ marginBottom: 0 }}
                      mode="local"
                      localPredecessors={pendingPredecessors}
                      onLocalChange={setPendingPredecessors}
                    />
                  </div>
                  {/* Responsible Party */}
                  <div style={atFieldGroup}>
                    <label style={atLabelStyle}>Responsible Party</label>
                    <input type="text" name="responsible_party" value={formData.responsible_party} onChange={handleFormChange}
                      style={atInputStyle} onFocus={handleFocus} onBlur={handleBlur}
                      placeholder="Person or company responsible" />
                  </div>
                  {/* Checkboxes - side by side */}
                  <div style={{ display: 'flex', gap: '24px', ...atFieldGroup }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                      <input type="checkbox" name="client_visible" checked={formData.client_visible} onChange={handleFormChange}
                        style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                      Client Visible
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                      <input type="checkbox" name="milestone_flag" checked={formData.milestone_flag} onChange={handleFormChange}
                        style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                      Milestone
                    </label>
                  </div>
                  {/* Notes */}
                  <div style={{ marginBottom: '0' }}>
                    <label style={atLabelStyle}>Notes</label>
                    <textarea name="notes" value={formData.notes} onChange={handleFormChange}
                      rows="2" placeholder="Additional notes"
                      style={{ ...atInputStyle, resize: 'vertical', minHeight: '60px' }}
                      onFocus={handleFocus} onBlur={handleBlur} />
                  </div>
                </div>
                {/* Footer */}
                <div style={{
                  display: 'flex', justifyContent: 'flex-end', gap: '10px',
                  padding: '16px 24px', borderTop: `1px solid ${t.tableBorder}`, flexShrink: 0,
                }}>
                  <button type="button"
                    onClick={() => { setShowModal(false); setFormErrors({}); setPendingPredecessors([]); }}
                    style={{
                      padding: '9px 22px', fontSize: '13px', fontWeight: 500,
                      border: `1px solid ${t.cardBorder}`, borderRadius: '8px', background: t.bg,
                      color: t.text, cursor: 'pointer',
                    }}
                  >Cancel</button>
                  <button type="button"
                    onClick={() => handleAddTask()}
                    style={{
                      padding: '9px 22px', fontSize: '13px', fontWeight: 600,
                      border: 'none', borderRadius: '8px',
                      background: '#3b82f6',
                      color: '#fff', cursor: 'pointer',
                    }}
                  >Create Task</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Task Properties Panel */}
      {editingTaskId && (() => {
        const currentTask = tasks.find(t => t.id === editingTaskId);
        const taskAllocs = taskAllocations[editingTaskId] || [];
        const pct = currentTask?.percent_complete || 0;

        // Shared input style
        const inputStyle = {
          width: '100%', padding: '8px 12px', fontSize: '13px',
          border: `1px solid ${t.cardBorder}`, borderRadius: '8px', outline: 'none',
          transition: 'border-color 0.2s', backgroundColor: t.bg,
          boxSizing: 'border-box',
        };
        const inputDisabledStyle = { ...inputStyle, backgroundColor: t.bgAlt, color: t.textSecondary, cursor: 'default' };
        const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
        const sectionTitleStyle = { fontSize: '12px', fontWeight: 700, color: t.text, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${t.tableBorder}` };
        const fieldGroup = { marginBottom: '14px' };

        return (
          <div className="modal-overlay" onClick={() => setEditingTaskId(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: t.bg, borderRadius: '16px', width: '580px', maxHeight: '88vh',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                animation: 'slideUp 0.25s ease',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                borderBottom: `1px solid ${t.tableBorder}`,
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{
                      ...getTaskTypeStyle(editFormData.task_type),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                    }}>
                      {editFormData.task_type}
                    </span>
                    <span style={{ fontSize: '12px', color: t.textMuted, fontWeight: 500 }}>{editFormData.task_code}</span>
                    <span style={{
                      ...getStatusStyle(editFormData.status),
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 500,
                    }}>
                      {editFormData.status || 'Not Started'}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: t.heading }}>
                    {editFormData.task_name || 'Task Properties'}
                  </h2>
                </div>
                <button
                  onClick={() => setEditingTaskId(null)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    color: t.textMuted, fontSize: '20px', lineHeight: 1, borderRadius: '6px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'; e.target.style.color = t.heading; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = t.textMuted; }}
                >
                  &times;
                </button>
              </div>

              {/* Scrollable body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

                {/* General section */}
                <div style={sectionTitleStyle}>General</div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Task Name</label>
                  <input type="text" name="task_name" value={editFormData.task_name} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={fieldGroup}>
                  <label style={labelStyle}>Description</label>
                  <textarea name="task_description" value={editFormData.task_description} onChange={handleEditFormChange} rows="2" style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Building Area / Phase</label>
                    <input type="text" name="building_area_phase" value={editFormData.building_area_phase} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Task Type</label>
                    <select name="task_type" value={editFormData.task_type} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Labor</option>
                      <option>Material</option>
                      <option>Procurement</option>
                      <option>Engineering</option>
                      <option>Inspection</option>
                      <option>Milestone</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Status</label>
                    <select name="status" value={editFormData.status || 'Not Started'} onChange={handleEditFormChange} style={inputStyle}>
                      <option>Not Started</option>
                      <option>In Progress</option>
                      <option>Complete</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Responsible Party</label>
                    <input type="text" name="responsible_party" value={editFormData.responsible_party} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                </div>

                {/* Schedule section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Schedule</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '12px', ...fieldGroup }}>
                  <div>
                    <label style={labelStyle}>Start Date</label>
                    <input type="date" name="start_date" value={editFormData.start_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Finish Date</label>
                    <input type="date" name="finish_date" value={editFormData.finish_date} onChange={handleEditFormChange} style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                  </div>
                  <div>
                    <label style={labelStyle}>Duration</label>
                    <input type="text" value={`${editDuration} days`} disabled style={inputDisabledStyle} />
                  </div>
                </div>

                {/* Progress slider */}
                {(() => {
                  const maxPct = getMaxProgress(currentTask);
                  // Round max down to nearest step of 5 so the slider aligns to step increments
                  const sliderMax = Math.floor(maxPct / 5) * 5;
                  return (
                    <div style={{ ...fieldGroup }}>
                      <label style={labelStyle}>
                        Progress — {pct}%
                        {maxPct < 100 && <span style={{ color: '#f59e0b', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> (max {sliderMax}% — schedule prorated to today)</span>}
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            type="range"
                            min="0"
                            max={sliderMax}
                            step="5"
                            value={Math.min(pct, sliderMax)}
                            onChange={(e) => {
                              const newPct = Math.min(parseInt(e.target.value, 10), sliderMax);
                              handlePercentCompleteChange(editingTaskId, newPct);
                            }}
                            style={{
                              width: '100%', height: '8px', cursor: sliderMax > 0 ? 'pointer' : 'not-allowed',
                              accentColor: pct >= 100 ? '#22c55e' : pct >= 50 ? '#3b82f6' : '#94a3b8',
                            }}
                            disabled={sliderMax === 0}
                          />
                          {maxPct < 100 && (
                            <div style={{
                              position: 'absolute', top: '-2px', height: '12px', right: 0,
                              width: `${100 - maxPct}%`, background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)',
                              borderRadius: '0 4px 4px 0', pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: t.text, minWidth: '40px', textAlign: 'right' }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Predecessors section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Predecessors</div>
                <PredecessorSection
                  currentTask={currentTask}
                  tasks={tasks}
                  editingTaskId={editingTaskId}
                  inputStyle={inputStyle}
                  fieldGroup={fieldGroup}
                  addPredecessor={addPredecessor}
                  updatePredecessor={updatePredecessor}
                  removePredecessor={removePredecessor}
                  fetchTasks={fetchTasks}
                  confirm={confirm}
                />

                {/* Dependent Tasks (successors) section */}
                {(() => {
                  const successors = tasks.filter(t =>
                    (t.predecessors || []).some(p => p.predecessor_task_id != null && Number(p.predecessor_task_id) === Number(editingTaskId))
                  );
                  return (
                    <div>
                      <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Dependent Tasks</div>
                      {successors.length === 0 ? (
                        <div style={{ fontSize: '13px', color: t.textMuted, padding: '8px 0' }}>
                          No tasks depend on this task
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
                          {successors.map(s => {
                            const pred = (s.predecessors || []).find(p => Number(p.predecessor_task_id) === Number(editingTaskId));
                            const depLabel = DEP_TYPES.find(d => d.value === pred?.dependency_type)?.short || pred?.dependency_type || 'FS';
                            const lagText = pred?.lag_days ? ` +${pred.lag_days}d` : '';
                            return (
                              <div key={s.id} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 10px', backgroundColor: t.bgAlt, borderRadius: '6px',
                                border: `1px solid ${t.cardBorder}`, fontSize: '13px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                  </svg>
                                  <div style={{ minWidth: 0 }}>
                                    <div>
                                      <span style={{ fontWeight: 500, color: t.text }}>{s.task_code}</span>
                                      <span style={{ color: t.textSecondary, marginLeft: '6px' }}>{s.task_name}</span>
                                    </div>
                                    {(s.start_date || s.finish_date) && (
                                      <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '2px' }}>
                                        {s.start_date ? `Start: ${fmtDate(s.start_date)}` : ''}
                                        {s.start_date && s.finish_date ? '  —  ' : ''}
                                        {s.finish_date ? `Finish: ${fmtDate(s.finish_date)}` : ''}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <span style={{
                                  fontSize: '11px', fontWeight: 600, color: '#8b5cf6',
                                  backgroundColor: '#f5f3ff', padding: '2px 8px', borderRadius: '10px',
                                }}>
                                  {depLabel}{lagText}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Options section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Options</div>
                <div style={{ display: 'flex', gap: '24px', ...fieldGroup }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="client_visible" checked={editFormData.client_visible} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Client Visible
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: t.text, cursor: 'pointer' }}>
                    <input type="checkbox" name="milestone_flag" checked={editFormData.milestone_flag} onChange={handleEditFormChange}
                      style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: 'pointer' }} />
                    Milestone
                  </label>
                </div>

                {/* SOV Links preview */}
                {taskAllocs.length > 0 && (
                  <>
                    <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>SOV Links</div>
                    <div style={{ ...fieldGroup }}>
                      {taskAllocs.map(a => {
                        const contractAmt = parseFloat(a.sov_contract_amount) || 0;
                        const pct = contractAmt > 0 ? Math.round((parseFloat(a.allocation_amount) / contractAmt) * 100) : 0;
                        return (
                          <div key={a.id} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', backgroundColor: t.bgAlt, borderRadius: '8px',
                            marginBottom: '6px', fontSize: '12px',
                          }}>
                            <div>
                              <span style={{ fontWeight: 600, color: t.text }}>{a.sov_code}</span>
                              <span style={{ color: t.textSecondary, marginLeft: '8px' }}>{a.sov_line_description}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: t.text }}>
                                ${(a.allocation_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                              <span style={{
                                fontSize: '11px', fontWeight: 600, color: '#3b82f6',
                                backgroundColor: '#eff6ff', padding: '2px 8px', borderRadius: '10px',
                              }}>
                                {pct}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Notes section */}
                <div style={{ ...sectionTitleStyle, marginTop: '8px' }}>Notes</div>
                <div style={fieldGroup}>
                  <textarea name="notes" value={editFormData.notes} onChange={handleEditFormChange} rows="3" style={{ ...inputStyle, resize: 'vertical', minHeight: '70px' }} placeholder="Add notes about this task..." onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = t.cardBorder} />
                </div>

              </div>
              {/* Footer — pinned outside scrollable area */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '16px 24px', borderTop: `1px solid ${t.tableBorder}`,
                flexShrink: 0,
              }}>
                <button type="button" onClick={() => { handleDeleteTask(editingTaskId); setEditingTaskId(null); }}
                  style={{
                    background: 'none', border: '1px solid #fecaca', color: '#dc2626',
                    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Delete
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setEditingTaskId(null)}
                  style={{
                    background: t.bgAlt, border: 'none', color: t.textSecondary,
                    padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.dark ? '#2d3748' : '#e2e8f0'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bgAlt; }}
                >
                  Cancel
                </button>
                <button type="button"
                  onClick={() => handleSaveEdit()}
                  style={{
                    background: '#3b82f6', border: 'none', color: '#fff',
                    padding: '8px 24px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    boxShadow: '0 1px 3px rgba(59,130,246,0.3)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* SOV Link Modal */}
      {linkingTaskId && (() => {
        const linkTask = tasks.find(t => t.id === linkingTaskId);
        if (!linkTask) return null;
        return (
          <SOVLinkModal
            task={linkTask}
            onClose={() => setLinkingTaskId(null)}
          />
        );
      })()}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default TasksTab;
